// components/ImageUploader.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Button, FormGroup, Label, Alert, Spinner } from 'reactstrap';
import { useSelector } from 'react-redux';
import ImageRecordsService from '../../helpers/imageRecords_helper';

const ImageUploader = ({ onImagesChange, maxImages = 10, existingImages = [], onCameraOpen, mostrarLabel = false }) => {
    const tenantId = useSelector((state) => state.Context.tenantId);
    const token = useSelector((state) => state.Token.token);
    const [imagenes, setImagenes] = useState(existingImages);
    
    // Keep a ref to imagenes so we can access current state even from unmounted component
    const imagenesRef = useRef(imagenes);
    useEffect(() => {
        console.log(`[USEEFFECT imagenes] Updating imagenesRef.current with ${imagenes.length} images`, {
            imageIds: imagenes.map(img => ({ id: img.id, recordUuid: img.recordUuid }))
        });
        imagenesRef.current = imagenes;
    }, [imagenes]);
    
    // Keep a ref to onImagesChange to ensure we always call the latest version
    const onImagesChangeRef = useRef(onImagesChange);
    useEffect(() => {
        onImagesChangeRef.current = onImagesChange;
    }, [onImagesChange]);
    
    // notifyParent: updates imagenesRef synchronously BEFORE setImagenes.
    // This is critical for the camera flow: when the camera unmounts this component,
    // React discards setImagenes calls, so imagenesRef (which was updated via useEffect)
    // stays stale. By updating imagenesRef.current directly here, the async upload
    // callback in the unmounted instance can still read the correct images list.
    const notifyParent = (images) => {
        console.log(`[NOTIFY PARENT] Updating imagenesRef.current with ${images.length} images`, {
            imageIds: images.map(img => ({ id: img.id, recordUuid: img.recordUuid }))
        });
        imagenesRef.current = images;
        console.log(`[NOTIFY PARENT] imagenesRef.current now has ${imagenesRef.current.length} images`);
        setImagenes(images);
        onImagesChangeRef.current(images);
    };
    
    // Sync internal state with existingImages prop when it changes
    // This is critical for when the component remounts after camera use
    // The old (unmounted) instance completes the upload and updates parent state
    // with recordUuid, but the new instance doesn't receive that update automatically
    useEffect(() => {
        console.log(`[SYNC] Syncing imagenes with existingImages`, {
            existingImagesCount: existingImages.length,
            currentImagenesCount: imagenes.length,
            existingIds: existingImages.map(img => ({ id: img.id, recordUuid: img.recordUuid, subido: img.subido })),
            currentIds: imagenes.map(img => ({ id: img.id, recordUuid: img.recordUuid, subido: img.subido }))
        });
        
        // Check if any image in existingImages has new data (e.g., recordUuid) 
        // that the internal state doesn't have yet
        const needsSync = existingImages.some(existingImg => {
            const currentImg = imagenes.find(img => img.id === existingImg.id);
            // Sync if image is new or if it has been updated (e.g., got recordUuid after upload)
            return !currentImg || 
                   existingImg.recordUuid !== currentImg.recordUuid || 
                   existingImg.subido !== currentImg.subido;
        });
        
        if (needsSync) {
            console.log(`[SYNC] State is stale, updating imagenes with existingImages`);
            setImagenes(existingImages);
        }
    }, [existingImages]);
    
    // Log component mount/unmount lifecycle
    useEffect(() => {
        console.log(`[LIFECYCLE] ImageUploader MOUNTED`, {
            existingImagesCount: existingImages.length,
            existingImages: existingImages.map(img => ({ id: img.id, nombre: img.nombre, recordUuid: img.recordUuid }))
        });
        
        return () => {
            console.log(`[LIFECYCLE] ImageUploader UNMOUNTING`, {
                currentImagenesCount: imagenes.length,
                currentImagenes: imagenes.map(img => ({ id: img.id, nombre: img.nombre, recordUuid: img.recordUuid }))
            });
        };
    }, []);
    
    useEffect(() => {
        console.log(`[ImageUploader] Component mounted with auth data`, {
            hasTenantId: !!tenantId,
            tenantId: tenantId,
            hasToken: !!token,
            tokenLength: token ? token.length : 0,
            existingImagesCount: existingImages.length
        });
        console.log(`[ImageUploader] Existing images: `, { existingImages });
    }, [tenantId, token, imagenes, existingImages]);


    const [error, setError] = useState('');
    // Removed uploading state as it was used for the manual upload button
    const [uploadProgress, setUploadProgress] = useState({});

    // Cargar PWA Elements para la cámara en web
    useEffect(() => {
        const loadPWAElements = async () => {
            if (typeof window !== 'undefined') {
                try {
                    const { defineCustomElements } = await import('@ionic/pwa-elements/loader');
                    defineCustomElements(window);
                } catch (error) {
                    console.log('PWA Elements no disponibles en este entorno');
                }
            }
        };

        loadPWAElements();
    }, []);

    /**
 * Convierte una URI local (webPath o path de Capacitor) a un objeto Blob/File.
 * @param imageUri La URI de la imagen obtenida de Camera.getPhoto().
 * @returns {Promise<Blob>} Los datos binarios de la imagen.
 */
    const convertToFile = async (imageUri) => {
        try {
            // Usa fetch() para obtener los datos de la URI temporal
            const response = await fetch(imageUri);
            // Convierte la respuesta a un objeto binario Blob
            const blob = await response.blob();
            return blob;
        } catch (error) {
            console.error("Error al convertir URI a Blob:", error);
            throw new Error("No se pudo obtener el archivo de la URI.");
        }
    }

    const tomarFoto = async () => {
        try {
            console.log(`[CAMERA DEBUG] tomarFoto started, current imagenes count: ${imagenes.length}`);
            if (imagenes.length >= maxImages) {
                setError(`Máximo ${maxImages} imágenes permitidas`);
                return;
            }

            const { Camera } = await import('@capacitor/camera');

            // Notificar al componente padre que la cámara se va a abrir
            console.log(`[CAMERA DEBUG] Calling onCameraOpen(true) - this will unmount ImageUploader`);
            if (onCameraOpen) {
                onCameraOpen(true);
            }


            const image = await Camera.getPhoto({
                quality: 85,
                allowEditing: false,
                resultType: 'uri',
                source: 'CAMERA',
                promptLabelHeader: 'Tomar Foto',
                promptLabelPhoto: 'Desde Galería',
                promptLabelPicture: 'Usar Cámara'
            });
            console.log(`[CAMERA DEBUG] Photo captured from camera`, {
                webPath: image.webPath,
                path: image.path,
                format: image.format,
                hasWebPath: !!image.webPath,
                hasPath: !!image.path
            });
            const imageFile = await convertToFile(image.webPath || image.path);
            console.log(`[CAMERA DEBUG] convertToFile completed`, {
                imageFileType: imageFile?.type,
                imageFileSize: imageFile?.size,
                imageFileConstructor: imageFile?.constructor?.name
            });
            // Notificar que la cámara se cerró
            console.log(`[CAMERA DEBUG] Calling onCameraOpen(false) - this will remount ImageUploader`);
            if (onCameraOpen) {
                onCameraOpen(false);
            }


            console.log(`[CAMERA DEBUG] Photo captured`, {
                imageFile,
                imageDetails: {
                    format: image.format,
                    webPath: image.webPath,
                }
            });
            if (imageFile) {
                const nuevaImagen = {
                    id: Date.now() + Math.random(),
                    formato: image.format,
                    fecha: new Date().toISOString(),
                    nombre: `foto_${new Date().getTime()}.${image.format}`,
                    file: imageFile,
                    path: image.webPath || image.path
                };

                console.log(`[CAMERA DEBUG] Created nuevaImagen`, {
                    id: nuevaImagen.id,
                    nombre: nuevaImagen.nombre,
                    hasFile: !!nuevaImagen.file,
                    fileSize: nuevaImagen.file?.size,
                    path: nuevaImagen.path
                });

                console.log(`[CAMERA DEBUG] Calling setImagenes and onImagesChange`);
                const currentImagenes = imagenesRef.current;
                const nuevas = [...currentImagenes, nuevaImagen];
                console.log(`[CAMERA DEBUG] Updating state, current count: ${currentImagenes.length}, nuevas count: ${nuevas.length}`);
                notifyParent(nuevas);
                setError('');

                // Trigger automatic upload
                console.log(`[CAMERA DEBUG] Calling handleUploadSingleImage`);
                handleUploadSingleImage(nuevaImagen, nuevas);
            }
        } catch (error) {
            // Asegurarse de notificar el cierre incluso en error
            if (onCameraOpen) {
                onCameraOpen(false);
            }
            if (error.message !== 'User cancelled photos app') {
                console.error('Error al tomar foto:', error);
                setError('Error al acceder a la cámara');
            }
        }
    };

    const seleccionarDeGaleria = async () => {
        try {
            console.log(`[GALLERY DEBUG] seleccionarDeGaleria started, current imagenes count: ${imagenes.length}`);
            if (imagenes.length >= maxImages) {
                setError(`Máximo ${maxImages} imágenes permitidas`);
                return;
            }

            const { Camera } = await import('@capacitor/camera');

            const image = await Camera.getPhoto({
                quality: 85,
                allowEditing: false,
                resultType: 'uri',
                source: 'PHOTOS',
                promptLabelHeader: 'Seleccionar Imagen',
                promptLabelPhoto: 'Desde Galería',
                promptLabelPicture: 'Usar Cámara'
            });
            console.log(`[GALLERY DEBUG] Photo selected from gallery`, {
                webPath: image.webPath,
                path: image.path,
                format: image.format,
                hasWebPath: !!image.webPath,
                hasPath: !!image.path
            });

            const imageFile = await convertToFile(image.webPath || image.path);
            console.log(`[GALLERY DEBUG] convertToFile completed`, {
                imageFileType: imageFile?.type,
                imageFileSize: imageFile?.size,
                imageFileConstructor: imageFile?.constructor?.name
            });


            if (image) {
                const nuevaImagen = {
                    id: Date.now() + Math.random(),
                    base64: image.base64String,
                    formato: image.format,
                    fecha: new Date().toISOString(),
                    nombre: `imagen_${new Date().getTime()}.${image.format}`,
                    file: imageFile,
                    path: image.webPath || image.path
                };

                console.log(`[GALLERY DEBUG] Created nuevaImagen`, {
                    id: nuevaImagen.id,
                    nombre: nuevaImagen.nombre,
                    hasFile: !!nuevaImagen.file,
                    fileSize: nuevaImagen.file?.size,
                    path: nuevaImagen.path
                });

                console.log(`[GALLERY DEBUG] Calling setImagenes and onImagesChange`);
                const currentImagenes = imagenesRef.current;
                const nuevas = [...currentImagenes, nuevaImagen];
                console.log(`[GALLERY DEBUG] Updating state, current count: ${currentImagenes.length}, nuevas count: ${nuevas.length}`);
                notifyParent(nuevas);
                setError('');

                // Trigger automatic upload
                console.log(`[GALLERY DEBUG] Calling handleUploadSingleImage`);
                handleUploadSingleImage(nuevaImagen, nuevas);
            }
        } catch (error) {
            if (error.message !== 'User cancelled photos app') {
                console.error('Error al seleccionar imagen:', error);
                setError('Error al acceder a la galería');
            }
        }
    };

    const eliminarImagen = (id) => {
        const nuevasImagenes = imagenesRef.current.filter(img => img.id !== id);
        notifyParent(nuevasImagenes);
        setError('');
    };

    const subirImagenServidor = async (imagen) => {
        const uploadStartTime = Date.now();

        try {
            // Log detailed info about the image being uploaded
            console.log(`[UPLOAD DEBUG] Preparing to upload image`, {
                id: imagen.id,
                nombre: imagen.nombre,
                formato: imagen.formato,
                hasFile: !!imagen.file,
                fileType: imagen.file?.type,
                fileSize: imagen.file?.size,
                path: imagen.path,
                hasBase64: !!imagen.base64
            });

            // Actualizar estado de progreso para esta imagen
            setUploadProgress(prev => ({
                ...prev,
                [imagen.id]: { uploading: true, progress: 0 }
            }));

            console.log(`[ImageUploader] Calling ImageRecordsService.uploadImage`);
            const result = await ImageRecordsService.uploadImage(imagen, { tenantId });

            const uploadDuration = Date.now() - uploadStartTime;
            
            // Log the complete response structure
            console.log(`[UPLOAD DEBUG] Backend response`, {
                resultKeys: Object.keys(result),
                resultData: result.data,
                resultId: result.id,
                resultSuccess: result.success,
                fullResult: result
            });
            
            console.log(`[ImageUploader] Upload completed in ${uploadDuration}ms`, {
                result,
                hasSuccess: !!result.success,
                hasData: !!result.data,
                hasId: !!result.id
            });

            // Check both possible response structures
            const backendId = result.id || result.data?.id;
            
            if (backendId) {
                // Actualizar imagen con la URL del servidor
                const imagenActualizada = {
                    ...imagen,
                    recordUuid: backendId,
                    subido: true
                };

                console.log(`[ImageUploader] Image updated successfully`, imagenActualizada);

                // Actualizar estado de progreso
                setUploadProgress(prev => ({
                    ...prev,
                    [imagen.id]: { uploading: false, progress: 100, success: true }
                }));

                return imagenActualizada;
            } else {
                console.error(`[ImageUploader] Upload failed - no ID in response`, {
                    result,
                    message: result.message
                });
                throw new Error(result.message || 'Error al subir imagen');
            }
        } catch (error) {
            const uploadDuration = Date.now() - uploadStartTime;

            console.error(`[ImageUploader] Upload failed after ${uploadDuration}ms`, {
                error: error.message,
                errorType: error.constructor.name,
                errorStack: error.stack,
                imageId: imagen.id,
                imageName: imagen.nombre,
                isTimeout: error.code === 'ECONNABORTED',
                hasTenantId: !!tenantId,
                hasToken: !!token
            });

            // Actualizar estado de progreso con error
            setUploadProgress(prev => ({
                ...prev,
                [imagen.id]: { uploading: false, progress: 0, error: error.message }
            }));

            throw error;
        }
    };

    const handleUploadSingleImage = async (imagen, imagenesActuales = null) => {
        try {
            console.log(`[UPLOAD DEBUG] handleUploadSingleImage called for image ${imagen.id}`);
            const imagenActualizada = await subirImagenServidor(imagen);

            console.log(`[UPLOAD DEBUG] Upload succeeded for image ${imagen.id}, recordUuid: ${imagenActualizada.recordUuid}`);
            
            // Use provided images or fall back to ref (for retry button)
            const currentImagenes = imagenesActuales || imagenesRef.current;
            const nuevasImagenes = currentImagenes.map(img =>
                img.id === imagen.id ? imagenActualizada : img
            );
            
            console.log(`[UPLOAD DEBUG] Updating state with ${nuevasImagenes.length} images`);
            
            // Update internal state and notify parent
            notifyParent(nuevasImagenes);
        } catch (error) {
            console.error("Auto upload failed for image", imagen.id, error);
            // Error state is already handled in uploadProgress by subirImagenServidor
        }
    };

    return (
        <FormGroup>
            {mostrarLabel && <Label>Imágenes</Label>}

            {error && (
                <Alert color="danger" className="py-2" timeout={0}>
                    <small>{error}</small>
                </Alert>
            )}

            {/* Botones de acción */}
            <div className="d-flex flex-wrap gap-2 mb-3">
                <Button
                    type="button"
                    color="primary"
                    outline
                    onClick={tomarFoto}
                    disabled={imagenes.length >= maxImages}
                    className="d-flex align-items-center gap-1"
                >
                    <i className="ri-camera-line me-1"></i>
                    Cámara
                </Button>

                <Button
                    type="button"
                    color="secondary"
                    outline
                    onClick={seleccionarDeGaleria}
                    disabled={imagenes.length >= maxImages}
                    className="d-flex align-items-center gap-1"
                >
                    <i className="ri-image-line me-1"></i>
                    Galería
                </Button>
            </div>

            {/* Contador de imágenes */}
            {maxImages && (
                <div className="mb-2">
                    <small className="text-muted">
                        {imagenes.length} / {maxImages} imágenes
                    </small>
                </div>
            )}

            {/* Vista previa de imágenes */}
            {imagenes.length > 0 && (
                <div className="row">
                    {imagenes.map((imagen) => (
                        <div key={imagen.id} className="col-6 col-md-4 col-lg-3 mb-3">
                            <div className="card h-100">
                                <div className="position-relative">
                                    <img
                                        src={imagen.path}
                                        alt="Vista previa"
                                        className="card-img-top"
                                        style={{
                                            height: '120px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                    <div className="position-absolute top-0 end-0 p-1">
                                        {imagen.subido ? (
                                            <span className="badge bg-success">
                                                <i className="ri-check-line"></i>
                                            </span>
                                        ) : uploadProgress[imagen.id]?.uploading ? (
                                            <span className="badge bg-primary">
                                                <Spinner size="sm" />
                                            </span>
                                        ) : uploadProgress[imagen.id]?.error ? (
                                            <span
                                                className="badge bg-danger"
                                                title={`${uploadProgress[imagen.id].error} - Click para reintentar`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleUploadSingleImage(imagen)}
                                            >
                                                <i className="ri-error-warning-line"></i>
                                            </span>
                                        ) : null}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm position-absolute top-0 start-0 m-1"
                                        onClick={() => eliminarImagen(imagen.id)}
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            padding: 0,
                                            fontSize: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="card-body p-2">
                                    <small className="text-muted d-block">
                                        {new Date(imagen.fecha).toLocaleTimeString()}
                                    </small>
                                    {imagen.subido && (
                                        <small className="text-success d-block">
                                            <i className="ri-check-line"></i> Subida
                                        </small>
                                    )}
                                    {uploadProgress[imagen.id]?.error && (
                                        <small className="text-danger d-block">
                                            <i className="ri-error-warning-line"></i> Error
                                        </small>
                                    )}
                                    {imagen.recordUuid && (
                                        <small className="text-info d-block">
                                            ID: {imagen.recordUuid.substring(0, 8)}...
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {imagenes.length === 0 && (
                <div className="text-center py-4 border rounded bg-light">
                    <i className="ri-image-line display-4 text-muted"></i>
                    <p className="text-muted mt-2 mb-0">No hay imágenes adjuntas</p>
                </div>
            )}
        </FormGroup>
    );
};

export default ImageUploader;